import {useQuery} from '@tanstack/react-query'

import {networkRetry} from '#/lib/async/retry'
import {ONE_MINUTE} from '#/lib/constants'
import {
  FALLBACK_GEOLOCATION_SERVICE_RESPONSE,
  GEOLOCATION_SERVICE_URL,
} from '#/geolocation/const'
import * as debug from '#/geolocation/debug'
import {logger} from '#/geolocation/logger'
import {type Geolocation, type GeoResponse} from '#/geolocation/types'
import {device} from '#/storage'

async function fetchGeolocationServiceData(
  url: string,
): Promise<Geolocation | undefined> {
  if (debug.enabled) return debug.resolve(debug.geolocation)
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`fetchGeolocationServiceData failed ${res.status}`)
  }
  const data = await (res.json() as Promise<Geolocation | GeoResponse>)
  if ((data as Geolocation)?.countryCode) {
    return data as Geolocation
  }
  return {
    countryCode: (data as GeoResponse)?.country_code,
    requiredValidation: (data as GeoResponse)?.required_validation,
    regionCode: (data as GeoResponse)?.region_code,
  }
}

/**
 * Local promise used within this file only.
 */
let geolocationServicePromise: Promise<{success: boolean}> | undefined

/**
 * Begin the process of resolving geolocation config. This is called right away
 * at app start, and the promise is awaited later before proceeding with app
 * startup.
 */
export async function resolve() {
  if (geolocationServicePromise) {
    const cached = device.get(['geolocationServiceResponse'])
    if (cached) {
      logger.debug(`resolve(): using cache`)
    } else {
      logger.debug(`resolve(): no cache`)
      const {success} = await geolocationServicePromise
      if (success) {
        logger.debug(`resolve(): resolved`)
      } else {
        logger.info(`resolve(): failed`)
      }
    }
  } else {
    logger.debug(`resolve(): initiating`)

    /**
     * THIS PROMISE SHOULD NEVER `reject()`! We want the app to proceed with
     * startup, even if geolocation resolution fails.
     */
    geolocationServicePromise = new Promise(async resolve => {
      let success = false

      function cacheResponseOrThrow(response: Geolocation | undefined) {
        if (response) {
          device.set(['geolocationServiceResponse'], response)
        } else {
          // endpoint should throw on all failures, this is insurance
          throw new Error(`fetchGeolocationServiceData returned no data`)
        }
      }

      try {
        // Try once, fail fast
        const config = await fetchGeolocationServiceData(
          GEOLOCATION_SERVICE_URL,
        )
        cacheResponseOrThrow(config)
        success = true
      } catch (e: any) {
        logger.debug(
          `resolve(): fetchGeolocationServiceData failed initial request`,
          {
            safeMessage: e.message,
          },
        )

        // retry 3 times, but don't await, proceed with default
        networkRetry(3, () =>
          fetchGeolocationServiceData(GEOLOCATION_SERVICE_URL),
        )
          .then(config => {
            cacheResponseOrThrow(config)
          })
          .catch((e: any) => {
            // complete fail closed
            logger.debug(
              `resolve(): fetchGeolocationServiceData failed retries`,
              {
                safeMessage: e.message,
              },
            )
          })
      } finally {
        resolve({success})
      }
    })
  }
}

export function useGeolocationServiceResponse() {
  const {data} = useQuery({
    queryKey: ['geolocation-service'],
    queryFn: async () => {
      const result = await fetchGeolocationServiceData(GEOLOCATION_SERVICE_URL)
      const geo = result ?? FALLBACK_GEOLOCATION_SERVICE_RESPONSE
      device.set(['geolocationServiceResponse'], geo)
      return geo
    },
    staleTime: ONE_MINUTE * 10,
    gcTime: ONE_MINUTE * 60,
    refetchInterval: ONE_MINUTE * 15,
    refetchOnWindowFocus: true,
    placeholderData:
      device.get(['geolocationServiceResponse']) ??
      FALLBACK_GEOLOCATION_SERVICE_RESPONSE,
  })

  return data ?? FALLBACK_GEOLOCATION_SERVICE_RESPONSE
}
